import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApplicationStore } from 'src/store/application';
import { useInsuranceStore } from 'src/store/insurance';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useFormSteps } from '@features/onboarding/hooks/useFormSteps';
import { FormStepSections } from '@features/onboarding/enums/formSections.enum';
import { ONBOARDING_STEP_KEY } from '@constants/index';
import { InlineIcon } from '@components/shared/inline-icon';

export const YourPlansPage = () => {
  const [isExpanded, setIsExpanded] = useState(true);

  const navigate = useNavigate();
  const {
    activeSubscriptionPlans,
    fetchActiveSubscriptionPlans,
    applications,
    getCurrentApplication,
    tier
  } = useApplicationStore();
  const { plans: allPlans, getPlanFirstDocument } = useInsuranceStore();
  const { setCurrentStep, setActiveDentalVisionCategory, medicalTags, planTags, setAiHighlightedPlan } = useOnboardingStore();
  const formSteps = useFormSteps();

  useEffect(() => {
    fetchActiveSubscriptionPlans();
    getCurrentApplication();
  }, [fetchActiveSubscriptionPlans, getCurrentApplication]);

  const dentalAndVisionCategories = useMemo(() => {
    const categories = allPlans
      .map((p) => p.category)
      .filter((c) => c && c !== 'Major Medical');
    const uniqueCategories = [...new Set(categories)];
    const dentalIndex = uniqueCategories.indexOf('Dental');
    if (dentalIndex > 0) {
      // Move Dental to the front
      const dentalCategory = uniqueCategories.splice(dentalIndex, 1);
      uniqueCategories.unshift(dentalCategory[0]);
    }
    return uniqueCategories;
  }, [allPlans]);

  const handleJumpTo = (sectionName: string, category?: string) => {
    const stepIndex = formSteps.findIndex((step) => step.sectionName === sectionName);
    if (stepIndex === -1) return;

    setCurrentStep(stepIndex);
    localStorage.setItem(ONBOARDING_STEP_KEY, stepIndex.toString());

    if (sectionName === FormStepSections.Dental && category) {
      const categoryIndex = dentalAndVisionCategories.indexOf(category);
      if (categoryIndex !== -1) {
        setActiveDentalVisionCategory(categoryIndex);
      }
    }
    navigate('/');
  };

  const getIconForPlan = (planCategory?: string) => {
    if (!planCategory) return '/icons/plus.svg';
    const category = planCategory.toLowerCase();
    if (category.includes('dental')) return '/icons/dental.svg';
    if (category.includes('vision')) return '/icons/vision.svg';
    if (category.includes('accident')) return '/icons/accident.svg';
    if (category.includes('hospital')) return '/icons/hospital.svg';
    return '/icons/plus.svg';
  };

  const displayedPlans = useMemo(() => {
    const planMap = new Map();

    const currentApplication = applications[0];
    const appPlansFromApplication = currentApplication?.plans || [];
    const hasActive = Array.isArray(activeSubscriptionPlans) && activeSubscriptionPlans.length > 0;
    const hasApplicationPlans = Array.isArray(appPlansFromApplication) && appPlansFromApplication.length > 0;

    // Decide source: default active subscriptions; if empty and applications exist, use application plans; else empty
    const sourcePlans = hasActive
      ? activeSubscriptionPlans.map((sp: any) => ({ id: sp.planId, applicants: sp.applicants, status: sp.status }))
      : (hasApplicationPlans ? appPlansFromApplication : []);

    // Prefer tags from store when available; fall back to plan applicants
    const parseTagIndices = (tags: string[] | undefined | null): number[] => {
      if (!Array.isArray(tags)) return [];
      return tags
        .map((t) => {
          const parts = String(t).split('_');
          const idx = parts[1];
          const num = Number(idx);
          return Number.isFinite(num) ? num : null;
        })
        .filter((n): n is number => n !== null);
    };

    const getCoveredNamesForPlan = (details: any, planLike: any): { applicants: string[] } => {
      let members: number[] = [];
      if (details?.category === 'Major Medical' && Array.isArray(medicalTags) && medicalTags.length > 0) {
        members = parseTagIndices(medicalTags);
      } else if (details && planTags && Array.isArray(planTags[String(details.id)]) && planTags[String(details.id)].length > 0) {
        members = parseTagIndices(planTags[String(details.id)]);
      } else {
        members = Array.isArray(planLike?.applicants) ? planLike.applicants : [];
      }

      const applicants = (currentApplication?.applicants || [])
        .filter((e: any, id: number) => {
          if (hasActive) return e && e.applicant !== 1;
          return members.includes(id) && e.applicant !== 1
        })
        .map((e: any) => e.firstName);
      return { applicants };
    };

    sourcePlans.forEach((planLike: any) => {
      const details = allPlans.find((p) => p.id === planLike.id);
      if (details && !planMap.has(details.id)) {
        const covered = getCoveredNamesForPlan(details, planLike);
        const docUrl = getPlanFirstDocument(details.id) || getPlanFirstDocument(details.uid);
        planMap.set(details.id, {
          id: details.id,
          uid: details.uid,
          name: details.name || details.marketingName,
          status: planLike.status ?? details.status,
          buttonText: 'Detail PDF',
          price: details.prices.find(e => e.tier === tier)?.price || details.prices[0].price,
          icon: getIconForPlan(details.category),
          category: details.category,
          coveredOthers: covered.applicants,
          onClick: () => {
            // setAiHighlightedPlan(details.uid);
            // handleJumpTo(
            //   details.category === 'Major Medical'
            //     ? FormStepSections.Medical
            //     : FormStepSections.Dental,
            //   details.category,
            // );
            if (docUrl) {
              window.open(docUrl, '_blank');
            } else {
              alert('No document available for this plan');
            }
          },
        });
      }
    });

    return Array.from(planMap.values());
  }, [activeSubscriptionPlans, applications, allPlans, formSteps, tier]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="flex flex-col gap-8">
      {displayedPlans.length > 0 ? (
        displayedPlans.map((plan, index) => (
          <section
            key={plan.id || index}
            className={`flex flex-col mt-5 bg-profile-section-bg max-l:mt-0 rounded-3xl ${
              isExpanded 
                ? 'pb-10 max-md:pb-8' 
                : 'pb-0'
            }`}
          >
            <div
              className={`bg-profile-header-bg text-white gap-6 items-center flex px-5 py-2 pb-2 tracking-wide max-md:py-2 cursor-pointer ${
                isExpanded ? 'rounded-t-3xl' : 'rounded-3xl'
              }`}
              onClick={toggleExpanded}
            >
              <InlineIcon
                src={plan.icon}
                className="w-10 h-10 icon-your-plan-page"
                classNameSVG="w-full h-full stroke-white"
                alt={''}
              />
              <p className="text-[2vw] max-lg:text-[4.5vw] flex-1">{plan.category} Plan</p>
              <svg
                className={`w-6 h-6 transition-transform duration-200 ${
                  isExpanded ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isExpanded ? 'max-h-500 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="flex flex-col px-9 max-md:px-5 mt-5">
                <div className="flex justify-between items-center max-md:flex-col">
                  <p className="font-bold text-4xl tracking-wide max-md:text-[5.2vw] max-xl:text-[3.2vw]">
                    {plan.name}
                  </p>
                  <hr className="lg:hidden mt-2" />
                  <div className="flex justify-between gap-2">
                    <p className="flex text-2xl max-md:text-[4vw] max-xl:text-[3vw] font-bold text-[#c5c5c5]">
                      <span>Premium:</span><span className="!text-black ml-2">${plan.price}</span>
                    </p>
                    <p className="flex text-2xl ml-[2vw] max-md:text-[4vw] max-xl:text-[3vw] font-bold text-[#c5c5c5]">
                      <span>Status:</span><span className="text-primary ml-2">{['Inactive', 'Active'][plan.status]}</span>
                    </p>
                  </div>
                </div>
                <hr className="mt-3" />
                <div className="flex max-lg:flex-col max-lg:gap-3 mt-8 justify-between w-[78%] max-m:w-full">
                  <div className="flex flex-col gap-2 w-1/2 max-lg:w-[90%] font-bold text-profile-secondary text-xl">
                    <span className="flex justify-between text-gray-700">
                      When your plan became active:{' '}
                      <p className="text-black">10/1/2025</p>
                    </span>
                    <span className="flex justify-between text-gray-700">
                      When the policy can renew:{' '}
                      <p className="text-black">10/1/2025</p>
                    </span>
                    <span className="flex justify-between text-gray-700">
                      Next billing date: <p className="text-black">11/1/2025</p>
                    </span>
                  </div>
                  <div className="flex flex-col w-[40%] max-lg:w-[90%] font-bold text-gray-700 text-xl">
                    <p className="flex max-md:justify-between">
                      <span className="md:min-w-[280px] mr-8">The type of plan this is:</span> <span className="text-black">Health</span>
                    </p>
                    <p className="flex max-md:justify-between">
                      <span className="md:min-w-[280px] mr-8">Insurance company (Carrier):{' '}</span>
                      <span className="text-black">Cigna</span>
                    </p>
                  </div>
                </div>
                <hr className="mt-9" />
                <div className="mt-6">
                  <p className="text-[2vw] font-normal tracking-wide max-lg:text-[4vw] max-m:text-[5vw] text-[#2e313f]">
                    Who is covered?
                  </p>
                  {/* TODO это доработать */}
                  <div className="flex mt-4.5 text-[2.7vw] font-medium gap-3 tracking-wider max-lg:text-[6vw]">
                    <p className="text-primary">You</p>
                    <span className="text-profile-header-bg">
                      {Array.isArray(plan.coveredOthers) && plan.coveredOthers.length > 0
                        ? ' ' + plan.coveredOthers.map((n: string) => `+ ${n}`).join(' ')
                        : ''}
                    </span>
                  </div>
                </div>
                {/* <hr className="mt-9" />
                <PlanHowItWorks deductible={1500} outOfPocketMax={12000} />
                */}
                <div className="mt-8 max-lg:mt-0 space-y-4">
                  <hr className="max-lg:hidden" />
                  <div className="flex justify-between items-center py-2 max-lg:pt-0 max-1m:flex-col max-1m:gap-5 max-1m:py-2">
                    <p className="text-[2vw] tracking-wide font-normal max-md:text-[2.5vw] max-m:text-[3.5vw] max-2xs:text-[4vw] max-m:text-center text-[#2e313f]">
                      You can view details about this plan here:
                    </p>
                    <button 
                      onClick={plan.onClick}
                      className="w-2xs bg-primary text-white tracking-wide px-10 max-md:text-base py-4 max-1xl:py-3 max-lg:text-xl rounded-full text-2xl hover:bg-primary/90 duration-200"
                    >
                      {plan.buttonText}
                    </button>
                  </div>
                  <hr className="" />
                  <div className="flex justify-between items-center py-2 max-1m:flex-col max-1m:gap-5 max-1m:py-2">
                    <p className="text-[2vw] tracking-wide font-normal max-md:text-[2.3vw] max-m:text-[3.5vw] max-2xs:text-[4vw] max-m:text-center text-[#2e313f]">
                      Need to make changes to this plan? Click here:
                    </p>
                    <button 
                      onClick={() => {
                        setAiHighlightedPlan(plan.uid);
                        handleJumpTo(
                          plan.category === 'Major Medical'
                            ? FormStepSections.Medical
                            : FormStepSections.Dental,
                          plan.category
                        );
                      }}
                      className="w-2xs bg-primary text-white tracking-wide px-10 max-md:text-base py-4 max-1xl:py-3 max-lg:text-xl rounded-full text-2xl hover:bg-primary/90 duration-200"
                    >
                      MAKE CHANGES
                    </button>
                  </div>
                  <hr className="" />
                  <div className="flex justify-between items-center py-2 max-1m:flex-col max-1m:gap-5 max-1m:py-2">
                    <p className="text-[2vw] tracking-wide font-normal max-md:text-[2.5vw] max-m:text-[3.5vw] max-2xs:text-[4vw] max-m:text-center text-[#2e313f]">
                      View your plan ID card here:
                    </p>
                    <button className="w-2xs bg-primary text-white tracking-wide max-md:text-base py-4 px-10 max-1xl:py-3 max-lg:text-xl rounded-full text-2xl hover:bg-primary/90 duration-200">
                      VIEW ID CARD
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ))
      ) : (
        <section className="flex flex-col mt-5 bg-profile-section-bg max-l:mt-0 rounded-3xl pb-10 max-md:pb-8">
          <div className="bg-profile-header-bg text-white gap-6 items-center flex px-5 py-2 pb-2 tracking-wide max-md:py-2 rounded-3xl">
            <img
              src="/icons/plus.svg"
              className="w-12 h-12"
              alt="no-plans"
            />
            <p className="text-[2vw] max-lg:text-[4.5vw] flex-1">No Plans Found</p>
          </div>
          <div className="flex flex-col px-9 max-md:px-5 mt-5">
            <div className="text-center p-10 flex flex-col items-center">
              <p className="text-2xl text-gray-600 mb-6">
                You currently have no active plans
              </p>
              <button
                onClick={() => handleJumpTo(FormStepSections.Medical)}
                className="px-4 max-m:m-auto max-1xl:text-base min-w-fit py-2 bg-primary rounded-full text-white text-xl font-semibold uppercase hover:bg-primary/90"
              >
                Select a Plan
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
